//#region header */
/**************************************************************************************************
//
//  Description: Configuration store for managing the state of the configuration details.
//
//  Copyright:    © 2025 - 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   21.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook         GMSCM-12 Added elasticSettingsSelected.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import { create } from "zustand";
import MultiTenantConfigGetDto from "../dtos/multiTenantConfigDto";
import ConfigErrorType from "../models/configErrorType";
import { mountStoreDevtool } from "simple-zustand-devtools";

interface ConfigQuery {
  configItem?: string;
  newConfig?: boolean;
  configSelected?: string;
  organisationSelected?: string;
  elasticSettingSelected?: string;
  elasticNodeSelected?: string;
  originalDocument?: MultiTenantConfigGetDto;
  configurationDocuments?: MultiTenantConfigGetDto[];
  clearFilter?: boolean;
  documentChanged?: boolean;
  disableSave?: boolean;
  revertChanges?: boolean;
  validationErrors?: ConfigErrorType[];
}

interface ConfigQueryStore {
  configQuery: ConfigQuery;
  setConfigItem: (configItem: string | undefined) => void;
  setNewConfig: (newConfig: boolean) => void;
  setConfigSelected: (configSelected: string | undefined) => void;
  setOrganisationSelected: (organisationSelected: string | undefined) => void;
  setElasticSettingSelected: (elasticSettingSelected: string | undefined) => void;
  setElasticNodeSelected: (elasticNodeSelected: string | undefined) => void;
  setOriginalDocument: (originalDocument: MultiTenantConfigGetDto) => void;
  setConfigurationDocuments: (configurationDocuments: MultiTenantConfigGetDto[]) => void;
  setClearFilter: (clearFilter: boolean) => void;
  setDocumentChanged: (documentChanged: boolean) => void;
  setDisableSave: (disableSave: boolean) => void;
  setRevertChanges: (revertChanges: boolean) => void;
  setValidationErrors: (validationErrors: ConfigErrorType[] | undefined) => void;
}

const useConfigQueryStore = create<ConfigQueryStore>((set) => ({
  configQuery: {},
  setConfigItem: (configItem: string | undefined) =>
    set((state) => ({ configQuery: { ...state.configQuery, configItem } })),
  setNewConfig: (newConfig: boolean) => set((state) => ({ configQuery: { ...state.configQuery, newConfig } })),
  setConfigSelected: (configSelected: string | undefined) =>
    set((state) => ({ configQuery: { ...state.configQuery, configSelected } })),
  setOrganisationSelected: (organisationSelected: string | undefined) =>
    set((state) => ({ configQuery: { ...state.configQuery, organisationSelected } })),
  setElasticSettingSelected: (elasticSettingSelected: string | undefined) =>
    set((state) => ({ configQuery: { ...state.configQuery, elasticSettingSelected } })),
  setElasticNodeSelected: (elasticNodeSelected: string | undefined) =>
    set((state) => ({ configQuery: { ...state.configQuery, elasticNodeSelected } })),
  setOriginalDocument: (originalDocument: MultiTenantConfigGetDto) =>
    set((state) => ({ configQuery: { ...state.configQuery, originalDocument } })),
  setConfigurationDocuments: (configurationDocuments: MultiTenantConfigGetDto[]) =>
    set((state) => ({ configQuery: { ...state.configQuery, configurationDocuments } })),
  setClearFilter: (clearFilter: boolean) => set((state) => ({ configQuery: { ...state.configQuery, clearFilter } })),
  setDocumentChanged: (documentChanged: boolean) =>
    set((state) => ({ configQuery: { ...state.configQuery, documentChanged } })),
  setDisableSave: (disableSave: boolean) => set((state) => ({ configQuery: { ...state.configQuery, disableSave } })),
  setRevertChanges: (revertChanges: boolean) =>
    set((state) => ({ configQuery: { ...state.configQuery, revertChanges } })),
  setValidationErrors: (validationErrors: ConfigErrorType[] | undefined) =>
    set((state) => ({ configQuery: { ...state.configQuery, validationErrors } })),
}));

if (process.env.NODE_ENV === "development") {
  mountStoreDevtool("Configuration Store", useConfigQueryStore);
}

export default useConfigQueryStore;
